import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import toast from "react-hot-toast";
import {
  CalendarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";

const formatLocalDateStr = (year, month, day) => {
  const m = String(month).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
};

const buildMonthRange = (year, month) => {
  const startStr = formatLocalDateStr(year, month, 1);
  const lastDay = new Date(year, month, 0).getDate();
  const endStr = formatLocalDateStr(year, month, lastDay);
  return { startStr, endStr };
};

const RiepilogoDisponibilita = () => {
  const { user: _user } = useAuth();
  const [riepilogo, setRiepilogo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [filterDate, setFilterDate] = useState(() => {
    const d = new Date();
    const { startStr, endStr } = buildMonthRange(
      d.getFullYear(),
      d.getMonth() + 1
    );
    return { inizio: startStr, fine: endStr };
  });
  const [showAttentionOnly, setShowAttentionOnly] = useState(false);
  const [volontariDettaglio, setVolontariDettaglio] = useState([]);
  const [expandedVolontari, setExpandedVolontari] = useState({});

  // Carica il riepilogo delle disponibilità
  const loadRiepilogo = useCallback(async () => {
    setLoading(true);
    try {
      const [response, volontariRes] = await Promise.all([
        api.get(
          `/disponibilita/riepilogo?data_inizio=${filterDate.inizio}&data_fine=${filterDate.fine}`
        ),
        api.get(
          `/disponibilita/riepilogo-volontari?data_inizio=${filterDate.inizio}&data_fine=${filterDate.fine}`
        ),
      ]);

      setRiepilogo(response.data);
      setVolontariDettaglio(volontariRes.data || []);
      setExpandedVolontari({});
    } catch (error) {
      console.error("Errore:", error);
      if (error.response?.status === 401) {
        toast.error("Sessione scaduta. Effettua nuovamente l'accesso.");
      } else {
        toast.error(error.response?.data?.message || "Errore nel caricamento del riepilogo");
      }
    } finally {
      setLoading(false);
    }
  }, [filterDate.inizio, filterDate.fine]);

  useEffect(() => {
    loadRiepilogo();
  }, [loadRiepilogo]);

  // Filtra i risultati per mostrare solo quelli critici o con attenzione
  const filteredRiepilogo = showAttentionOnly
    ? riepilogo.filter((item) => item.critico || item.attenzione)
    : riepilogo;

  // Raggruppa per data
  const groupedByDate = filteredRiepilogo.reduce((acc, item) => {
    if (!acc[item.data]) {
      acc[item.data] = [];
    }
    acc[item.data].push(item);
    return acc;
  }, {});

  // Ottieni il nome del giorno
  const getDayName = (dateString) => {
    const date = new Date(dateString);
    const days = [
      "Domenica",
      "Lunedì",
      "Martedì",
      "Mercoledì",
      "Giovedì",
      "Venerdì",
      "Sabato",
    ];
    return days[date.getDay()];
  };

  const handleMonthChange = (direction) => {
    setCurrentMonth((prev) => {
      const next = { ...prev, month: prev.month + direction };
      if (next.month === 0) {
        next.month = 12;
        next.year -= 1;
      } else if (next.month === 13) {
        next.month = 1;
        next.year += 1;
      }
      const { startStr, endStr } = buildMonthRange(next.year, next.month);
      setFilterDate({ inizio: startStr, fine: endStr });
      return next;
    });
  };

  // Formatta la data
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  };

  const formatTime = (timeValue) => String(timeValue || "").slice(0, 5);

  // Calcola statistiche
  const stats = {
    total: riepilogo.length,
    critico: riepilogo.filter((item) => item.critico).length,
    attention: riepilogo.filter((item) => item.attenzione && !item.critico).length,
    sufficient: riepilogo.filter((item) => item.sufficiente).length,
  };

  const volontariDisponibili = volontariDettaglio.filter(
    (volontario) =>
      !volontario.indisponibile_mese &&
      (volontario.disponibilita_totali || 0) > 0
  );

  const volontariAssenti = volontariDettaglio.filter(
    (volontario) =>
      !volontario.indisponibile_mese &&
      (volontario.disponibilita_totali || 0) === 0
  );

  const volontariIndisponibili = volontariDettaglio.filter(
    (volontario) => volontario.indisponibile_mese
  );

  const toggleVolontarioDetails = (volontarioId) => {
    setExpandedVolontari((prev) => ({
      ...prev,
      [volontarioId]: !prev[volontarioId],
    }));
  };

  const formatDetailDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("it-IT", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  };

  const getRelazioneBadgeClasses = (relazioneCongregazione) =>
    relazioneCongregazione === "delegato"
      ? "bg-amber-100 text-amber-800"
      : "bg-blue-100 text-blue-800";

  const getRelazioneLabel = (relazioneCongregazione) =>
    relazioneCongregazione === "delegato" ? "Delegato" : "Proclamatore";

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Riepilogo Disponibilità
          </h1>
          <p className="text-gray-600 mt-1">
            Panoramica delle disponibilità per l'autocompilazione dei turni
          </p>
        </div>
        <button
          onClick={loadRiepilogo}
          className="btn-primary flex items-center justify-center md:w-auto w-full"
        >
          <ArrowPathIcon className="h-5 w-5 mr-2" />
          Aggiorna
        </button>
      </div>

      {/* Statistiche */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <CalendarIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Totale Slot</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Sufficienti</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.sufficient}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Attenzione</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.attention}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <XCircleIcon className="h-8 w-8 text-red-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Critici</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.critico}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleMonthChange(-1)}
              className="p-2 border rounded-md hover:bg-gray-100"
              aria-label="Mese precedente"
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <div className="text-lg font-semibold">
              {new Date(currentMonth.year, currentMonth.month - 1).toLocaleDateString(
                "it-IT",
                { month: "long", year: "numeric" }
              )}
            </div>
            <button
              onClick={() => handleMonthChange(1)}
              className="p-2 border rounded-md hover:bg-gray-100"
              aria-label="Mese successivo"
            >
              <ChevronRightIcon className="h-5 w-5 text-gray-600" />
            </button>
          </div>
          <div className="flex items-center">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showAttentionOnly}
                onChange={(e) => setShowAttentionOnly(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                Mostra solo slot critici o con attenzione
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Lista disponibilità */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Disponibilità ({filteredRiepilogo.length} slot)
          </h3>
        </div>
        <div className="overflow-x-auto">
          {Object.keys(groupedByDate).length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              Nessuna disponibilità trovata per il periodo selezionato
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {Object.entries(groupedByDate).map(([data, items]) => (
                <div key={data} className="p-6">
                  <div className="flex items-center mb-4">
                    <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                    <h4 className="text-lg font-medium text-gray-900">
                      {getDayName(data)} {formatDate(data)}
                    </h4>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                    {items.map((item, index) => {
                      // Determina il colore e l'icona in base allo stato
                      let cardClasses = "";
                      let iconComponent = null;
                      
                      if (item.critico) {
                        cardClasses = "border-red-300 bg-red-50";
                        iconComponent = <XCircleIcon className="h-5 w-5 text-red-600" />;
                      } else if (item.attenzione) {
                        cardClasses = "border-yellow-300 bg-yellow-50";
                        iconComponent = <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />;
                      } else {
                        cardClasses = "border-green-300 bg-green-50";
                        iconComponent = <CheckCircleIcon className="h-5 w-5 text-green-600" />;
                      }

                      return (
                      <div
                        key={index}
                        className={`border rounded-lg p-4 ${cardClasses}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h5 className="font-medium text-gray-900">
                              {item.postazione}
                            </h5>
                            <p className="text-sm text-gray-600">
                              {formatTime(item.orario_inizio)} - {formatTime(item.orario_fine)}
                            </p>
                          </div>
                          {iconComponent}
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <span className="text-gray-500">Totali:</span>
                            <span className="ml-1 font-medium">
                              {item.totale_disponibili}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Uomini:</span>
                            <span
                              className={`ml-1 font-medium ${
                                item.uomini === 0
                                  ? "text-red-600"
                                  : "text-green-600"
                              }`}
                            >
                              {item.uomini}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Donne:</span>
                            <span className="ml-1 font-medium text-blue-600">
                              {item.donne}
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          Max {item.max_volontari} volontari per slot
                        </div>
                        {item.critico && (
                          <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-800">
                            🔴 Critico: {item.totale_disponibili < item.max_volontari 
                              ? `Solo ${item.totale_disponibili} disponibile/i su ${item.max_volontari} richiesti`
                              : "Nessun uomo disponibile e non ci sono abbastanza persone"}
                          </div>
                        )}
                        {item.attenzione && !item.critico && (
                          <div className="mt-2 p-2 bg-yellow-100 rounded text-xs text-yellow-800">
                            ⚠️ Attenzione: Nessun uomo disponibile per questo turno
                          </div>
                        )}
                      </div>
                    )})}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Informazioni */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-blue-900">
              Informazioni per l'autocompilazione
            </h3>
            <div className="mt-1 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>
                  Gli slot <span className="font-medium text-red-600">critici (rosso)</span>{" "}
                  non hanno abbastanza disponibili o mancano uomini
                </li>
                <li>
                  Gli slot con <span className="font-medium text-yellow-600">attenzione (giallo)</span>{" "}
                  hanno abbastanza persone ma nessun uomo disponibile
                </li>
                <li>
                  Gli slot <span className="font-medium text-green-600">sufficienti (verde)</span>{" "}
                  hanno abbastanza disponibili e almeno 1 uomo
                </li>
                <li>
                  Utilizza i filtri per concentrarti sui periodi specifici
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Tabella volontari */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Volontari della congregazione
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Elenco completo di proclamatori e delegati del mese selezionato.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700">
              Totale {volontariDettaglio.length}
            </span>
            <span className="px-3 py-1 rounded-full bg-green-100 text-green-800">
              Con disponibilità {volontariDisponibili.length}
            </span>
            <span className="px-3 py-1 rounded-full bg-red-100 text-red-800">
              Senza disponibilità {volontariAssenti.length}
            </span>
            <span className="px-3 py-1 rounded-full bg-sky-100 text-sky-800">
              Non disponibili {volontariIndisponibili.length}
            </span>
          </div>
        </div>

        {volontariDettaglio.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">
            Nessun volontario attivo trovato per la congregazione e il mese selezionati.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Volontario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Disponibilità mese
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Dettaglio
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {volontariDettaglio.map((volontario) => {
                  const isExpanded = Boolean(expandedVolontari[volontario.id]);

                  return (
                    <React.Fragment key={volontario.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <button
                            type="button"
                            className="flex items-center gap-3 text-left"
                            onClick={() => toggleVolontarioDetails(volontario.id)}
                          >
                            {isExpanded ? (
                              <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {volontario.nome} {volontario.cognome}
                              </div>
                              <div className="text-xs text-gray-500">
                                {volontario.indisponibile_mese
                                  ? "Non disponibile questo mese"
                                  : `${volontario.disponibilita_totali} disponibilità questo mese`}
                              </div>
                            </div>
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getRelazioneBadgeClasses(
                              volontario.relazione_congregazione
                            )}`}
                          >
                            {getRelazioneLabel(volontario.relazione_congregazione)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {volontario.indisponibile_mese ? (
                            <span className="inline-flex rounded-full bg-sky-100 px-2.5 py-1 text-xs font-medium text-sky-800">
                              Non disponibile
                            </span>
                          ) : (
                            volontario.disponibilita_totali
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            type="button"
                            className="text-sm font-medium text-primary-600 hover:text-primary-700"
                            onClick={() => toggleVolontarioDetails(volontario.id)}
                          >
                            {isExpanded ? "Nascondi" : "Mostra"}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan={4} className="px-6 py-5">
                            {volontario.postazioni.length === 0 ? (
                              <p className="text-sm text-gray-500">
                                {volontario.indisponibile_mese
                                  ? "Il volontario ha dichiarato di non essere disponibile nel mese selezionato."
                                  : "Nessuna disponibilità inserita nel mese selezionato."}
                              </p>
                            ) : (
                              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                {volontario.postazioni.map((postazione) => (
                                  <div
                                    key={`${volontario.id}-${postazione.postazione_id}`}
                                    className="rounded-lg border border-gray-200 bg-white p-4"
                                  >
                                    <h4 className="text-sm font-semibold text-gray-900">
                                      {postazione.postazione}
                                    </h4>
                                    <div className="mt-3 space-y-2">
                                      {postazione.disponibilita.map((item) => (
                                        <div
                                          key={`${volontario.id}-${postazione.postazione_id}-${item.data}-${item.slot_orario_id}`}
                                          className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700"
                                        >
                                          <div>
                                            <span className="font-medium capitalize">
                                              {formatDetailDate(item.data)}
                                            </span>
                                            {" "}
                                            {formatTime(item.orario_inizio)}-{formatTime(item.orario_fine)}
                                          </div>
                                          <div
                                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                                              item.assegnato
                                                ? "bg-green-100 text-green-800"
                                                : "bg-gray-200 text-gray-700"
                                            }`}
                                            title={
                                              item.assegnato
                                                ? "Turno assegnato a questo volontario"
                                                : "Disponibilità non ancora assegnata"
                                            }
                                          >
                                            {item.assegnato ? (
                                              <CheckCircleIcon className="h-4 w-4" />
                                            ) : (
                                              <XCircleIcon className="h-4 w-4" />
                                            )}
                                            {item.assegnato ? "Assegnato" : "Non assegnato"}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiepilogoDisponibilita;
